import { useState } from 'react';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import { deleteProject } from '@/utils/project_delete_utils';

type Props = {
    open: boolean;
    projectId: number;
    projectLabel: string;
    listPath: string;
    onClose: () => void;
};

export const DeleteProjectDialog = ({
    open,
    projectId,
    projectLabel,
    listPath,
    onClose,
}: Props) => {
    const notify = useNotify();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);

    const close = () => {
        if (busy) return;
        onClose();
    };

    const submit = async (keepOnedrive: boolean) => {
        setBusy(true);
        try {
            await deleteProject(projectId, keepOnedrive);
            notify('Project deleted', { type: 'success' });
            onClose();
            navigate(listPath);
        } catch (error) {
            notify(
                error instanceof Error ? error.message : 'Delete failed',
                { type: 'error' }
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
            <DialogTitle>Delete project</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    You are about to delete {projectLabel}. This will soft-delete all quotes
                    and project files from the database.
                    <br />
                    <br />
                    <strong>Delete</strong> will also remove files from OneDrive/SharePoint.
                    <br />
                    <strong>Delete (keep OneDrive files)</strong> will only delete from the database.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={close} disabled={busy}>
                    Cancel
                </Button>
                <Button
                    color="error"
                    variant="outlined"
                    onClick={() => void submit(true)}
                    disabled={busy}
                    startIcon={
                        busy ? (
                            <CircularProgress color="inherit" size={16} />
                        ) : undefined
                    }
                >
                    {busy ? 'Deleting…' : 'Delete (keep OneDrive files)'}
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={() => void submit(false)}
                    disabled={busy}
                    startIcon={
                        busy ? (
                            <CircularProgress color="inherit" size={16} />
                        ) : undefined
                    }
                >
                    {busy ? 'Deleting…' : 'Delete'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
